import { useEffect, useRef } from 'react';

/**
 * THE background-refresh poll for the order list pages.
 *
 * Product owner, 2026-08-08: *"add a 30 second poll"* — the follow-up to
 * removing the `Refresh` button from Department Orders. With no button and no
 * poll, a full browser reload was the only way to see an order someone else
 * had raised, and a reload discards expanded rows and any in-progress inline
 * edit. This removes the need for the reload rather than putting the button
 * back.
 *
 * ══════════════════════════════════════════════════════════════════════
 * 🚨 WHY THIS IS A HOOK AND NOT A `setInterval` IN EACH PAGE
 * ══════════════════════════════════════════════════════════════════════
 * The interval is the trivial part. The four rules around it — pause while an
 * edit is dirty, pause while the tab is hidden, never stack requests, never
 * surface an error — are what a copy drifts on, and each has a live instance in
 * this codebase. Three inline `useEffect`s would be three chances to forget one,
 * and the one most likely to be forgotten is the dirty guard, whose failure is
 * silent data loss.
 *
 * ── THE FOUR RULES, AND WHY EACH IS THERE ─────────────────────────────
 *
 * 1. 🚨 **NEVER REFETCH WHILE AN INLINE EDIT IS DIRTY.** `DepartmentOrders.js`
 *    has expand-to-edit; a refetch landing mid-edit re-renders the list and can
 *    discard typing the user did nothing to cause. **That exact failure already
 *    happened here** through a cross-tab `storage` listener (retired 2026-10-18 with
 *    the order-list layout toggle), and the accepted
 *    fix was to DEFER rather than block or swallow. This follows that
 *    precedent: a tick that lands while `paused` is
 *    **skipped**, and the next natural tick after the flag clears fetches.
 *    🔑 **Missed ticks are NOT queued.** Catching up would fire a burst of
 *    requests the moment a long edit was saved — the opposite of what a poll is
 *    for — and the data is a snapshot either way, so one fresh fetch is worth
 *    exactly as much as five.
 *
 * 2. **PAUSE WHEN THE TAB IS HIDDEN**, and fetch ONCE immediately on becoming
 *    visible again. This app is left open all day; polling a background tab for
 *    hours is wasted server load for data nobody is looking at. The
 *    fetch-on-return is what stops the user staring at stale rows for up to a
 *    full interval after switching back — without it, hiding the tab makes the
 *    page *worse* than not polling at all.
 *
 * 3. **NEVER STACK REQUESTS.** If a tick fires while the previous fetch is
 *    still in flight, it is skipped rather than piled on. A slow server would
 *    otherwise turn a 30s poll into an ever-growing queue of overlapping
 *    requests, which is how a poll becomes an outage.
 *    ⚠️ **This is an IN-FLIGHT guard, not a stale-response guard, and the two
 *    are different things** — see `hooks/useSearchRequest.js`, whose docblock
 *    records that a timer never closes the overlap window. It is sufficient
 *    HERE only because there is at most one poll request at a time and every
 *    caller's fetch writes the same whole-list state from the same endpoint, so
 *    a late arrival cannot paint a narrower result over a wider one. **If a
 *    caller ever polls something order-dependent, it needs `useLatestRequest`
 *    as well as this.**
 *
 * 4. 🔑 **ERRORS ARE SILENT.** The user did not ask for this request and must
 *    not be punished for its failure: a failed poll must never paint an error
 *    banner over good data or clear the list. It is caught, logged at
 *    `console.debug`, and the next tick tries again. **This is why callers pass
 *    a fetch that does not set error state** — `DepartmentOrders`'
 *    `fetchOrders(true)` is silent about the SPINNER but still calls
 *    `setError`, so the poll uses its own suppressed variant. Wiring a poll to a
 *    fetch that sets error state re-creates exactly the defect this rule
 *    forbids.
 *
 * ── WHAT IT DELIBERATELY DOES NOT DO ──────────────────────────────────
 * It does not touch expanded rows, scroll position or any detail cache: it
 * calls the function it is given and owns nothing else. Keeping the payload
 * decision in the page is what lets `DepartmentOrders` refresh the LIST without
 * disturbing `orderDetails`.
 *
 * @param {() => Promise<any>} onTick  the fetch. Must be silent about loading
 *   AND about errors; the hook catches anything that escapes.
 * @param {object}  [options]
 * @param {number}  [options.intervalMs=POLL_INTERVAL_MS]
 * @param {boolean} [options.enabled=true]  false disables the poll entirely.
 * @param {boolean} [options.paused=false]  true skips ticks (the dirty guard).
 * @param {boolean} [options.skipWhenHidden=true]  rule 2. `false` keeps ticking
 *   in a hidden tab (the fetch-on-return still fires). ONE caller: the
 *   notification bell (`components/Layout.js`), whose tick is a count-only
 *   request and whose hidden-tab OS popup needs it (controller ruling
 *   2026-09-24). The order pages never pass it. Pinned: the DEFAULT by §3b
 *   of the test, and WHO may pass it by §3c (a comment-stripped scan: only
 *   this file and Layout.js name it in production code).
 */

/**
 * 30 seconds — the owner's number, named so changing it is one edit.
 * ⚠️ It is also the visible-tab request rate per open page, so raising the
 * frequency multiplies server load by every user with the page open all day.
 */
export const POLL_INTERVAL_MS = 30000;

export default function usePollingRefresh(onTick, options = {}) {
  const { intervalMs = POLL_INTERVAL_MS, enabled = true, paused = false, skipWhenHidden = true } = options;

  // 🔑 Refs, not effect dependencies. `onTick` is a new closure on most renders
  // and `paused` flips on every keystroke in a dirty editor — putting either in
  // the dependency array would tear down and rebuild the interval constantly,
  // so the timer would never actually reach its own period.
  const onTickRef = useRef(onTick);
  const pausedRef = useRef(paused);
  const skipWhenHiddenRef = useRef(skipWhenHidden);
  const inFlightRef = useRef(false);

  useEffect(() => { onTickRef.current = onTick; }, [onTick]);
  useEffect(() => { pausedRef.current = paused; }, [paused]);
  useEffect(() => { skipWhenHiddenRef.current = skipWhenHidden; }, [skipWhenHidden]);

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;

    const run = async (reason) => {
      // Rule 1 — an edit is dirty. Skip; do not queue.
      if (pausedRef.current) return;
      // Rule 2 — nobody is looking (unless the caller opted out, see above).
      if (skipWhenHiddenRef.current && typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      // Rule 3 — the previous fetch has not come back.
      if (inFlightRef.current) return;

      inFlightRef.current = true;
      try {
        await onTickRef.current();
      } catch (err) {
        // Rule 4 — silent. Never surfaced to the user.
        // eslint-disable-next-line no-console
        console.debug(`[poll:${reason}] refresh failed, will retry next tick`, err);
      } finally {
        // 🪤 Cleared even when the component has unmounted mid-flight. The ref
        // outlives the effect, and leaving it `true` would permanently disable
        // polling for the next mount that reuses it.
        if (!cancelled) inFlightRef.current = false;
        else inFlightRef.current = false;
      }
    };

    const id = setInterval(() => run('tick'), intervalMs);

    // Rule 2's other half: coming BACK to the tab fetches immediately rather
    // than waiting out the rest of the interval.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') run('visible');
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      // Rule 4 of the brief — a leaked interval on a route change keeps
      // fetching for the life of the tab.
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, intervalMs]);
}
